import { Subject, debounceTime } from 'rxjs';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { Keyword } from './keyword';
import { KeywordEdit } from './keyword-edit';
import { StyleGroup } from './style-group';
import { ScriptXml } from './script-xml';

type I = { start: number; end: number; cls: string };

export class Highlight$ {
    private _styleMap = new Map<string, StyleGroup>();
    hitStyle?: StyleGroup;
    keywordStyle?: StyleGroup;

    private _fieldMap = new Map<string, string[]>();

    private _scriptXMLMap = new Map<string, ScriptXml>();

    charReplace = {
        '|': '∫',
    };

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(public coords: CoordinatorService) {
        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    onStart() {
        this.coords.template$.repository$.keywordGroupStyleParams.forEach((params) => {
            if (params.isKeywordDefault) {
                delete params.isKeywordDefault;
                this.keywordStyle = new StyleGroup(this.coords, { id: '_keyword', style: params });
                this.setStyle(this.keywordStyle);
            } else if (params.isSearchDefault) {
                delete params.isSearchDefault;
                this.hitStyle = new StyleGroup(this.coords, {
                    id: '_hit',
                    style: params,
                });
                this.setStyle(this.hitStyle);
            }
        });
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    clear() {
        this._styleMap.clear();
        this.onStart();
    }

    runYear(y: string | number) {
        if (typeof y === 'number') y = y.toString();
        if (!this.coords.field$.get('year')?.isActive) return y;
        return `<span class="_hit" style="${this.coords.search$.highlightStyle.replaceAll(/\s+/g, ' ').trim()}">${y}</span>`;
    }

    private _manifestStore() {
        const styleIdSet = new Set<string>(['_keyword', '_hit']);
        this.coords.keyword$.sortedGroupLs.forEach((group) => {
            styleIdSet.add(group.styleId);
        });

        this.coords.manifest$.store({
            type: 'style',
            id: `styleLs_${this.coords.project$.currentId}`,
            valueMap: new Map<string, any[]>([
                [
                    'styleLs',
                    Array.from(this._styleMap.values())
                        .filter((style) => styleIdSet.has(style.id))
                        .map((sg) => sg.save()),
                ],
            ]),
        });
    }

    export(isStandalone = true) {
        const ls: [string, string[]][] = [['styleLs', ['style']]];
        if (isStandalone) {
            this.coords.export$.export(
                'style-list',
                `styles_${this.coords.project$.currentTitle}`,
                ls,
            );
        }
        return ls;
    }

    import(ls: any[]) {
        ls.forEach(([_, x]: [string, any]) => {
            const style = x.styleLs.map((x: any) => new StyleGroup(this.coords, x));
            this.coords.highlight$.setStyle(style);
        });

        this.markForManifestStore();
    }

    load3(params: any) {
        if (!params) return;

        params.styleLs?.forEach((x: any) => {
            try {
                const style = new StyleGroup(this.coords, x);
                this.setStyle(style);
            } catch (e) {
                this.coords.utils$.consoleError('HIGHLIGHT$ LOAD3 ERROR', e);
            }
        });
    }

    getStyle(tag: string) {
        return this._styleMap.get(tag);
    }

    setStyle(sg: StyleGroup) {
        this._styleMap.set(sg.id, sg);
    }

    run(txt: string, type: string) {
        let scriptXML = this._scriptXMLMap.get(type);
        if (!scriptXML) {
            scriptXML = this._prepareScriptXML(type);
        }
        return scriptXML!.parse(txt);
    }

    setField(field: string, value: string[]) {
        const currentVal = this._fieldMap.get(field);
        if (!currentVal && !value.length) return;
        if (currentVal && this.coords.utils$.equal(currentVal, value)) return;
        this._fieldMap.set(field, value);
        if (field == 'text') {
            this._scriptXMLMap.delete('title');
            this._scriptXMLMap.delete('abstract');
        } else this._scriptXMLMap.delete(field);
        // console.log('SET FIELD', field, value);
    }

    getField(field: string) {
        return this._fieldMap.get(field)?.map((x) => x) || [];
    }

    reset() {
        this._scriptXMLMap.clear();
    }

    private _prepareScriptXML(type: string) {
        const scriptXML = new ScriptXml(this.coords);
        scriptXML.addHighlights(type);
        if (['title', 'abstract'].includes(type)) {
            if (this.coords.keyword$.isHighlight) scriptXML.addKeywords();
        }
        this._scriptXMLMap.set(type, scriptXML);
        return scriptXML;
    }
}
